import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

interface Game {
  _id: string;
  title: string;
  description: string;
  shortDescription: string;
  category: string;
  platform: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  ageRange: string;
  estimatedTime: string;
  gameType: 'Adventure' | 'Puzzle' | 'Competition' | 'Creative' | 'Simulation' | 'Multiplayer';
  isActive: boolean;
  isFeatured: boolean;
  popularity: number;
  rating: number;
  playCount: number;
  completionRate: number;
  averagePlayTime: number;
  bannerImage: string;
  screenshots: string[];
  demoVideo: string;
  tags: string[];
  learningObjectives: string[];
  skills: string[];
  hardware: string[];
  software: string[];
  gameMechanics: string[];
  story: string;
  rules: string[];
  scoring: string;
  progression: {
    levels: number;
    milestones: string[];
    unlockables: string[];
  };
  socialFeatures: {
    multiplayer: boolean;
    sharing: boolean;
    collaboration: boolean;
    competition: boolean;
  };
  accessibility: {
    visual: boolean;
    auditory: boolean;
    motor: boolean;
    cognitive: boolean;
  };
  setup: string[];
  gameplay: string[];
  challenges: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  achievements: {
    name: string;
    description: string;
    requirements: string;
    reward: string;
  }[];
  leaderboards: string[];
  resources: {
    tutorials: string[];
    videos: string[];
    documentation: string[];
    community: string[];
  };
  safety: string[];
  troubleshooting: {
    commonIssues: string[];
    solutions: string[];
  };
  extensions: string[];
  realWorldApplications: string[];
  educatorNotes: string;
  parentGuide: string;
  difficultyProgression: {
    level: number;
    requirements: string[];
    rewards: string[];
  }[];
  adaptiveLearning: {
    enabled: boolean;
    algorithm: string;
    personalization: string[];
  };
  analytics: {
    tracking: string[];
    metrics: string[];
    insights: string[];
  };
}

const Games: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [selectedAgeRange, setSelectedAgeRange] = useState('All');
  const [selectedGameType, setSelectedGameType] = useState('All');
  const [sortBy, setSortBy] = useState('popularity');

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/highcode/games`);
        if (response.ok) {
          const data = await response.json();
          setGames(data);
        } else {
          // Fallback to sample games if API fails
          console.log('API failed, using sample games');
          setGames([
            {
              _id: 'code-quest-castle',
              title: 'Code Quest Castle',
              description: 'Navigate through a magical castle by writing code. Solve puzzles, defeat dragons, and rescue the princess!',
              shortDescription: 'An interactive adventure game where kids code their way through a castle.',
              category: 'Adventure',
              platform: 'Scratch',
              difficulty: 'Easy',
              ageRange: '6-12',
              estimatedTime: '1-2 hours',
              gameType: 'Adventure',
              isActive: true,
              isFeatured: true,
              popularity: 1200,
              rating: 4.8,
              playCount: 850,
              completionRate: 75,
              averagePlayTime: 90,
              bannerImage: '/images/games/code-quest-castle-banner.png',
              screenshots: ['/images/games/code-quest-castle-ss1.png', '/images/games/code-quest-castle-ss2.png'],
              demoVideo: 'https://www.youtube.com/embed/example-castle',
              tags: ['Scratch', 'adventure', 'logic', 'storytelling'],
              learningObjectives: ['Basic sequencing', 'Conditional logic', 'Looping constructs', 'Debugging'],
              skills: ['Problem-solving', 'Computational thinking', 'Creativity'],
              hardware: ['Computer', 'Internet'],
              software: ['Scratch 3.0 (online or offline)'],
              gameMechanics: ['Drag-and-drop coding', 'Character movement', 'Object interaction', 'Puzzle solving'],
              story: 'The evil sorcerer has trapped Princess Pixel in the highest tower of Code Castle. Only a brave coder can navigate the castle\'s enchanted halls and free her!',
              rules: ['Use code blocks to move your character.', 'Collect keys to unlock doors.', 'Avoid traps and monsters.'],
              scoring: 'Points for collecting treasures and completing levels quickly.',
              progression: {
                levels: 10,
                milestones: ['Enter the castle', 'Find the first key', 'Defeat the mini-boss', 'Reach the princess'],
                unlockables: ['New character skins', 'Special code blocks']
              },
              socialFeatures: {
                multiplayer: false,
                sharing: true,
                collaboration: false,
                competition: false
              },
              accessibility: {
                visual: true,
                auditory: true,
                motor: true,
                cognitive: true
              },
              setup: ['Open Scratch editor.', 'Load the "Code Quest Castle" starter project.', 'Follow on-screen instructions.'],
              gameplay: ['Drag code blocks to the script area.', 'Click the green flag to run your code.', 'Watch your character move and interact.'],
              challenges: {
                beginner: ['Move character to a specific spot.', 'Make character jump over an obstacle.'],
                intermediate: ['Create a loop to collect multiple items.', 'Use an "if-then" block to avoid a moving enemy.'],
                advanced: ['Design a complex path using nested loops and conditionals.', 'Create a custom animation for your character.']
              },
              achievements: [
                { name: 'First Step', description: 'Complete the tutorial level.', requirements: 'Finish Level 1', reward: 'Bronze Badge' },
                { name: 'Loop Master', description: 'Use a loop effectively 10 times.', requirements: 'Use "repeat" block 10 times', reward: 'Silver Badge' }
              ],
              leaderboards: ['Speedrun - Fastest completion time.'],
              resources: {
                tutorials: ['Scratch Basics Tutorial', 'Code Quest Castle Walkthrough'],
                videos: ['https://www.youtube.com/watch?v=scratch-tutorial'],
                documentation: ['Scratch Wiki'],
                community: ['Scratch Online Community Forums']
              },
              safety: ['Ensure adult supervision for younger children.', 'Take breaks to avoid eye strain.'],
              troubleshooting: {
                commonIssues: ['Character not moving', 'Code not running'],
                solutions: ['Check if blocks are connected correctly.', 'Ensure green flag is clicked.']
              },
              extensions: ['Design new levels.', 'Add more enemies and treasures.', 'Create a two-player mode.'],
              realWorldApplications: ['Game development', 'Robotics control', 'Interactive storytelling'],
              educatorNotes: 'This project introduces fundamental programming concepts in a fun, engaging way. Encourage students to experiment and debug their code.',
              parentGuide: 'Help your child read the instructions and understand the logic. Celebrate their achievements!',
              difficultyProgression: [
                { level: 1, requirements: ['Basic movement'], rewards: ['New character sprite'] },
                { level: 5, requirements: ['Use conditionals'], rewards: ['Unlock "Magic Spell" block'] }
              ],
              adaptiveLearning: {
                enabled: true,
                algorithm: 'difficulty_adjustment',
                personalization: ['Hints for struggling players', 'Bonus challenges for advanced players']
              },
              analytics: {
                tracking: ['Level completion', 'Time spent per level', 'Block usage'],
                metrics: ['Average completion time', 'Most used blocks'],
                insights: ['Common sticking points for learners']
              }
            },
            {
              _id: 'arduino-led-challenge',
              title: 'Arduino LED Challenge',
              description: 'Learn Arduino programming by controlling LEDs in this interactive sequence game!',
              shortDescription: 'A fun physical computing game to learn basic electronics and timing.',
              category: 'Electronics',
              platform: 'Arduino',
              difficulty: 'Medium',
              ageRange: '9-13',
              estimatedTime: '2-3 hours',
              gameType: 'Competition',
              isActive: true,
              isFeatured: false,
              popularity: 800,
              rating: 4.5,
              playCount: 600,
              completionRate: 60,
              averagePlayTime: 120,
              bannerImage: '/images/games/led-target-practice-banner.png',
              screenshots: ['/images/games/led-target-practice-ss1.png'],
              demoVideo: 'https://www.youtube.com/embed/example-led-game',
              tags: ['Arduino', 'electronics', 'timing', 'game'],
              learningObjectives: ['Digital I/O', 'Loops and delays', 'Button input', 'Circuit building'],
              skills: ['Electronics assembly', 'C++ programming', 'Precision timing'],
              hardware: ['Arduino Uno', 'Breadboard', 'LEDs (Red, Yellow, Green)', 'Resistors (220 Ohm)', 'Push button', 'Jumper wires'],
              software: ['Arduino IDE'],
              gameMechanics: ['Sequential LED lighting', 'Button press detection', 'Score keeping'],
              story: 'Become the ultimate LED sharpshooter! Program your Arduino to create a moving target and test your reflexes.',
              rules: ['Press the button when the green LED lights up.', 'Red LED is a miss, yellow is a warning.', 'Score points for accurate hits.'],
              scoring: '10 points for a green hit, -5 for a red hit. Highest score wins.',
              progression: {
                levels: 5,
                milestones: ['Build the circuit', 'Program basic sequence', 'Add button input', 'Implement scoring'],
                unlockables: ['Faster LED sequences', 'More complex patterns']
              },
              socialFeatures: {
                multiplayer: true,
                sharing: true,
                collaboration: false,
                competition: true
              },
              accessibility: {
                visual: true,
                auditory: true,
                motor: true,
                cognitive: true
              },
              setup: ['Assemble the circuit on a breadboard.', 'Connect Arduino to computer.', 'Open Arduino IDE and load sketch.'],
              gameplay: ['Upload the code to Arduino.', 'Watch LEDs light up.', 'Press the button at the right moment.'],
              challenges: {
                beginner: ['Make LEDs light up one by one.', 'Detect a button press.'],
                intermediate: ['Create a random LED sequence.', 'Implement a scoring system.'],
                advanced: ['Add a timer for faster gameplay.', 'Integrate a display for score and countdown.']
              },
              achievements: [
                { name: 'First Circuit', description: 'Successfully build the LED circuit.', requirements: 'Complete circuit setup', reward: 'Circuit Builder Badge' },
                { name: 'Sharpshooter', description: 'Achieve a score of 100 points.', requirements: 'Score 100 points', reward: 'Gold Medal' }
              ],
              leaderboards: ['High Score - Highest score in a single game.'],
              resources: {
                tutorials: ['Arduino Blink Tutorial', 'Button Input Tutorial'],
                videos: ['https://www.youtube.com/watch?v=arduino-led-game'],
                documentation: ['Arduino Language Reference'],
                community: ['Arduino Forums']
              },
              safety: ['Ensure correct wiring to avoid short circuits.', 'Do not connect Arduino to high voltage.'],
              troubleshooting: {
                commonIssues: ['LEDs not lighting up', 'Button not responding'],
                solutions: ['Check wiring connections.', 'Verify code logic for button input.']
              },
              extensions: ['Add more LEDs and buttons.', 'Create different game modes.', 'Connect to a mobile app via Bluetooth.'],
              realWorldApplications: ['Industrial automation', 'Interactive displays', 'Home security systems'],
              educatorNotes: 'This project is excellent for teaching basic electronics and C++ programming. Emphasize safety and systematic debugging.',
              parentGuide: 'Help your child with circuit assembly and ensure they follow safety guidelines. Encourage them to customize the game!',
              difficultyProgression: [
                { level: 1, requirements: ['Basic LED control'], rewards: ['New LED color unlocked'] },
                { level: 3, requirements: ['Random sequences'], rewards: ['Unlock "Fast Mode"'] }
              ],
              adaptiveLearning: {
                enabled: true,
                algorithm: 'performance_based',
                personalization: ['Adjust LED speed based on performance', 'Suggest relevant tutorials for common errors']
              },
              analytics: {
                tracking: ['Hit/miss ratio', 'Game duration', 'Code complexity'],
                metrics: ['Average accuracy', 'Time to complete levels'],
                insights: ['Areas where users struggle with timing or logic']
              }
            }
          ]);
        }
      } catch (err) {
        console.log('API error, using sample games');
        // Use sample games as fallback
        setGames([
          {
            _id: 'code-quest-castle',
            title: 'Code Quest Castle',
            description: 'Navigate through a magical castle by writing code. Solve puzzles, defeat dragons, and rescue the princess!',
            shortDescription: 'An interactive adventure game where kids code their way through a castle.',
            category: 'Adventure',
            platform: 'Scratch',
            difficulty: 'Easy',
            ageRange: '6-12',
            estimatedTime: '1-2 hours',
            gameType: 'Adventure',
            isActive: true,
            isFeatured: true,
            popularity: 1200,
            rating: 4.8,
            playCount: 850,
            completionRate: 75,
            averagePlayTime: 90,
            bannerImage: '/images/games/code-quest-castle-banner.png',
            screenshots: ['/images/games/code-quest-castle-ss1.png', '/images/games/code-quest-castle-ss2.png'],
            demoVideo: 'https://www.youtube.com/embed/example-castle',
            tags: ['Scratch', 'adventure', 'logic', 'storytelling'],
            learningObjectives: ['Basic sequencing', 'Conditional logic', 'Looping constructs', 'Debugging'],
            skills: ['Problem-solving', 'Computational thinking', 'Creativity'],
            hardware: ['Computer', 'Internet'],
            software: ['Scratch 3.0 (online or offline)'],
            gameMechanics: ['Drag-and-drop coding', 'Character movement', 'Object interaction', 'Puzzle solving'],
            story: 'The evil sorcerer has trapped Princess Pixel in the highest tower of Code Castle. Only a brave coder can navigate the castle\'s enchanted halls and free her!',
            rules: ['Use code blocks to move your character.', 'Collect keys to unlock doors.', 'Avoid traps and monsters.'],
            scoring: 'Points for collecting treasures and completing levels quickly.',
            progression: {
              levels: 10,
              milestones: ['Enter the castle', 'Find the first key', 'Defeat the mini-boss', 'Reach the princess'],
              unlockables: ['New character skins', 'Special code blocks']
            },
            socialFeatures: {
              multiplayer: false,
              sharing: true,
              collaboration: false,
              competition: false
            },
            accessibility: {
              visual: true,
              auditory: true,
              motor: true,
              cognitive: true
            },
            setup: ['Open Scratch editor.', 'Load the "Code Quest Castle" starter project.', 'Follow on-screen instructions.'],
            gameplay: ['Drag code blocks to the script area.', 'Click the green flag to run your code.', 'Watch your character move and interact.'],
            challenges: {
              beginner: ['Move character to a specific spot.', 'Make character jump over an obstacle.'],
              intermediate: ['Create a loop to collect multiple items.', 'Use an "if-then" block to avoid a moving enemy.'],
              advanced: ['Design a complex path using nested loops and conditionals.', 'Create a custom animation for your character.']
            },
            achievements: [
              { name: 'First Step', description: 'Complete the tutorial level.', requirements: 'Finish Level 1', reward: 'Bronze Badge' },
              { name: 'Loop Master', description: 'Use a loop effectively 10 times.', requirements: 'Use "repeat" block 10 times', reward: 'Silver Badge' }
            ],
            leaderboards: ['Speedrun - Fastest completion time.'],
            resources: {
              tutorials: ['Scratch Basics Tutorial', 'Code Quest Castle Walkthrough'],
              videos: ['https://www.youtube.com/watch?v=scratch-tutorial'],
              documentation: ['Scratch Wiki'],
              community: ['Scratch Online Community Forums']
            },
            safety: ['Ensure adult supervision for younger children.', 'Take breaks to avoid eye strain.'],
            troubleshooting: {
              commonIssues: ['Character not moving', 'Code not running'],
              solutions: ['Check if blocks are connected correctly.', 'Ensure green flag is clicked.']
            },
            extensions: ['Design new levels.', 'Add more enemies and treasures.', 'Create a two-player mode.'],
            realWorldApplications: ['Game development', 'Robotics control', 'Interactive storytelling'],
            educatorNotes: 'This project introduces fundamental programming concepts in a fun, engaging way. Encourage students to experiment and debug their code.',
            parentGuide: 'Help your child read the instructions and understand the logic. Celebrate their achievements!',
            difficultyProgression: [
              { level: 1, requirements: ['Basic movement'], rewards: ['New character sprite'] },
              { level: 5, requirements: ['Use conditionals'], rewards: ['Unlock "Magic Spell" block'] }
            ],
            adaptiveLearning: {
              enabled: true,
              algorithm: 'difficulty_adjustment',
              personalization: ['Hints for struggling players', 'Bonus challenges for advanced players']
            },
            analytics: {
              tracking: ['Level completion', 'Time spent per level', 'Block usage'],
              metrics: ['Average completion time', 'Most used blocks'],
              insights: ['Common sticking points for learners']
            }
          },
          {
            _id: 'arduino-led-challenge',
            title: 'Arduino LED Challenge',
            description: 'Learn Arduino programming by controlling LEDs in this interactive sequence game!',
            shortDescription: 'A fun physical computing game to learn basic electronics and timing.',
            category: 'Electronics',
            platform: 'Arduino',
            difficulty: 'Medium',
            ageRange: '9-13',
            estimatedTime: '2-3 hours',
            gameType: 'Competition',
            isActive: true,
            isFeatured: false,
            popularity: 800,
            rating: 4.5,
            playCount: 600,
            completionRate: 60,
            averagePlayTime: 120,
            bannerImage: '/images/games/led-target-practice-banner.png',
            screenshots: ['/images/games/led-target-practice-ss1.png'],
            demoVideo: 'https://www.youtube.com/embed/example-led-game',
            tags: ['Arduino', 'electronics', 'timing', 'game'],
            learningObjectives: ['Digital I/O', 'Loops and delays', 'Button input', 'Circuit building'],
            skills: ['Electronics assembly', 'C++ programming', 'Precision timing'],
            hardware: ['Arduino Uno', 'Breadboard', 'LEDs (Red, Yellow, Green)', 'Resistors (220 Ohm)', 'Push button', 'Jumper wires'],
            software: ['Arduino IDE'],
            gameMechanics: ['Sequential LED lighting', 'Button press detection', 'Score keeping'],
            story: 'Become the ultimate LED sharpshooter! Program your Arduino to create a moving target and test your reflexes.',
            rules: ['Press the button when the green LED lights up.', 'Red LED is a miss, yellow is a warning.', 'Score points for accurate hits.'],
            scoring: '10 points for a green hit, -5 for a red hit. Highest score wins.',
            progression: {
              levels: 5,
              milestones: ['Build the circuit', 'Program basic sequence', 'Add button input', 'Implement scoring'],
              unlockables: ['Faster LED sequences', 'More complex patterns']
            },
            socialFeatures: {
              multiplayer: true,
              sharing: true,
              collaboration: false,
              competition: true
            },
            accessibility: {
              visual: true,
              auditory: true,
              motor: true,
              cognitive: true
            },
            setup: ['Assemble the circuit on a breadboard.', 'Connect Arduino to computer.', 'Open Arduino IDE and load sketch.'],
            gameplay: ['Upload the code to Arduino.', 'Watch LEDs light up.', 'Press the button at the right moment.'],
            challenges: {
              beginner: ['Make LEDs light up one by one.', 'Detect a button press.'],
              intermediate: ['Create a random LED sequence.', 'Implement a scoring system.'],
              advanced: ['Add a timer for faster gameplay.', 'Integrate a display for score and countdown.']
            },
            achievements: [
              { name: 'First Circuit', description: 'Successfully build the LED circuit.', requirements: 'Complete circuit setup', reward: 'Circuit Builder Badge' },
              { name: 'Sharpshooter', description: 'Achieve a score of 100 points.', requirements: 'Score 100 points', reward: 'Gold Medal' }
            ],
            leaderboards: ['High Score - Highest score in a single game.'],
            resources: {
              tutorials: ['Arduino Blink Tutorial', 'Button Input Tutorial'],
              videos: ['https://www.youtube.com/watch?v=arduino-led-game'],
              documentation: ['Arduino Language Reference'],
              community: ['Arduino Forums']
            },
            safety: ['Ensure correct wiring to avoid short circuits.', 'Do not connect Arduino to high voltage.'],
            troubleshooting: {
              commonIssues: ['LEDs not lighting up', 'Button not responding'],
              solutions: ['Check wiring connections.', 'Verify code logic for button input.']
            },
            extensions: ['Add more LEDs and buttons.', 'Create different game modes.', 'Connect to a mobile app via Bluetooth.'],
            realWorldApplications: ['Industrial automation', 'Interactive displays', 'Home security systems'],
            educatorNotes: 'This project is excellent for teaching basic electronics and C++ programming. Emphasize safety and systematic debugging.',
            parentGuide: 'Help your child with circuit assembly and ensure they follow safety guidelines. Encourage them to customize the game!',
            difficultyProgression: [
              { level: 1, requirements: ['Basic LED control'], rewards: ['New LED color unlocked'] },
              { level: 3, requirements: ['Random sequences'], rewards: ['Unlock "Fast Mode"'] }
            ],
            adaptiveLearning: {
              enabled: true,
              algorithm: 'performance_based',
              personalization: ['Adjust LED speed based on performance', 'Suggest relevant tutorials for common errors']
            },
            analytics: {
              tracking: ['Hit/miss ratio', 'Game duration', 'Code complexity'],
              metrics: ['Average accuracy', 'Time to complete levels'],
              insights: ['Areas where users struggle with timing or logic']
            }
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGames();
  }, []);

  const filteredGames = games.filter(game => {
    const matchesSearch = game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         game.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         game.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || game.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'All' || game.difficulty === selectedDifficulty;
    const matchesAgeRange = selectedAgeRange === 'All' || game.ageRange === selectedAgeRange;
    const matchesGameType = selectedGameType === 'All' || game.gameType === selectedGameType;

    return matchesSearch && matchesCategory && matchesDifficulty && matchesAgeRange && matchesGameType;
  });

  const sortedGames = [...filteredGames].sort((a, b) => {
    switch (sortBy) {
      case 'popularity':
        return b.popularity - a.popularity;
      case 'rating':
        return b.rating - a.rating;
      case 'playCount':
        return b.playCount - a.playCount;
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  const categories = ['All', ...Array.from(new Set(games.map(game => game.category)))];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard'];
  const ageRanges = ['All', ...Array.from(new Set(games.map(game => game.ageRange)))];
  const gameTypes = ['All', 'Adventure', 'Puzzle', 'Competition', 'Creative', 'Simulation', 'Multiplayer'];

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              🎮 <span className="bg-gradient-to-r from-yellow-400 to-pink-400 bg-clip-text text-transparent">
                Gamified Learning
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Learn coding through fun, interactive games designed for kids aged 4-18
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-white/20 px-3 py-1 rounded-full">🏆 Achievements</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">🎯 Challenges</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">👥 Multiplayer</span>
              <span className="bg-white/20 px-3 py-1 rounded-full">📊 Leaderboards</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <input
                type="text"
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
            </div>

            {/* Age Range Filter */}
            <div>
              <select
                value={selectedAgeRange}
                onChange={(e) => setSelectedAgeRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {ageRanges.map(ageRange => (
                  <option key={ageRange} value={ageRange}>{ageRange}</option>
                ))}
              </select>
            </div>

            {/* Game Type Filter */}
            <div>
              <select
                value={selectedGameType}
                onChange={(e) => setSelectedGameType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {gameTypes.map(gameType => (
                  <option key={gameType} value={gameType}>{gameType}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort Options */}
          <div className="mt-4 flex flex-wrap gap-4 items-center">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'popularity', label: 'Popularity' },
                { value: 'rating', label: 'Rating' },
                { value: 'playCount', label: 'Most Played' },
                { value: 'title', label: 'Alphabetical' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    sortBy === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {sortedGames.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No games found</h3>
            <p className="text-gray-600">Try adjusting your filters to find more games.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedGames.map((game) => (
              <div key={game._id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Game Banner */}
                <div className="relative h-48 bg-gradient-to-br from-blue-500 to-purple-600">
                  {game.bannerImage ? (
                    <img
                      src={game.bannerImage}
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl">🎮</span>
                    </div>
                  )}
                  
                  {/* Featured Badge */}
                  {game.isFeatured && (
                    <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold">
                      ⭐ Featured
                    </div>
                  )}

                  {/* Difficulty Badge */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      game.difficulty === 'Easy' ? 'bg-green-500 text-white' :
                      game.difficulty === 'Medium' ? 'bg-yellow-500 text-white' :
                      'bg-red-500 text-white'
                    }`}>
                      {game.difficulty}
                    </span>
                  </div>
                </div>

                {/* Game Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900 line-clamp-2">
                      {game.title}
                    </h3>
                    <div className="flex items-center text-yellow-500 ml-2">
                      <span className="text-sm font-medium">{game.rating.toFixed(1)}</span>
                      <span className="ml-1">⭐</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {game.shortDescription}
                  </p>

                  {/* Game Info */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                      {game.category}
                    </span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                      {game.platform}
                    </span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                      {game.ageRange}
                    </span>
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">
                      {game.gameType}
                    </span>
                  </div>

                  {/* Game Mechanics */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {game.gameMechanics.slice(0, 3).map((mechanic, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {mechanic}
                      </span>
                    ))}
                    {game.gameMechanics.length > 3 && (
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        +{game.gameMechanics.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Social Features */}
                  <div className="flex items-center gap-2 mb-4">
                    {game.socialFeatures.multiplayer && (
                      <span className="text-green-600" title="Multiplayer">👥</span>
                    )}
                    {game.socialFeatures.sharing && (
                      <span className="text-blue-600" title="Sharing">📤</span>
                    )}
                    {game.socialFeatures.collaboration && (
                      <span className="text-purple-600" title="Collaboration">🤝</span>
                    )}
                    {game.socialFeatures.competition && (
                      <span className="text-red-600" title="Competition">🏆</span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex justify-between text-sm text-gray-500 mb-4">
                    <span>⏱️ {game.estimatedTime}</span>
                    <span>👥 {game.playCount} plays</span>
                    <span>📈 {game.completionRate}% completion</span>
                  </div>

                  {/* Action Button */}
                  <Link
                    to={`/high-code/game/${game._id}`}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-center block"
                  >
                    🎮 Play Game
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Gaming and Learning?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of kids who are already learning to code through fun, interactive games!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/start-building"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              🚀 Start Building
            </Link>
            <Link
              to="/all-projects"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              📚 View All Projects
            </Link>
            <Link
              to="/high-code"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
            >
              🏠 Back to High Code
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Games;
